import React, { useState } from 'react';

/**
 * Batch 22 — Marking guide panel.
 *
 * Shows the hidden, assessor-only marking guide for the task beside the AI
 * review, so the assessor can check the AI's view against the agreed
 * mandatory items without leaving the page. The guide is never shown to
 * students and is not (yet) used by the AI itself — that is a later batch.
 *
 * Props: guide = the marking_guide object from GET /api/submissions/:id
 *        (id, status, version, source, kind, title, guide{...}, approved_by_name, approved_at)
 *        cot = true for the AMDRAS Certificate of Training (mentoring language)
 */
export default function MarkingGuidePanel({ guide, cot }) {
  const [open, setOpen] = useState(true);
  const [showSample, setShowSample] = useState(false);
  const [checked, setChecked] = useState({});

  if (!guide || !guide.guide) return null;
  const g = guide.guide;
  const isDraft = guide.status !== 'approved';

  const labels = cot ? {
    mandatory: 'Required for the task to be complete',
    nice: 'Developmental quality signals',
    sample: 'What a complete, well-done task looks like',
    supp: 'Mentor follow-up prompt example',
    check: 'Trainer check required before relying on this guide',
    panel: 'Mentoring guide for this task',
  } : {
    mandatory: 'Mandatory items (all required for Satisfactory)',
    nice: 'Nice-to-have items (never grounds for resubmission)',
    sample: 'Sample strong answer',
    supp: 'Supplementary question example',
    check: 'Assessor check required before relying on this guide',
    panel: 'Marking guide for this task',
  };

  const toggle = (id) => setChecked(c => ({ ...c, [id]: !c[id] }));
  const mandatory = g.mandatory || [];
  const ticked = mandatory.filter(m => checked[m.id]).length;

  return (
    <div className="card marking-guide" style={{ marginTop: 16, borderLeft: `4px solid ${isDraft ? '#D97706' : '#047857'}` }}>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>{open ? '▾' : '▸'}</span>
          <span>{labels.panel}</span>
          <span className={`badge ${isDraft ? 'badge-pending' : 'badge-graded'}`} style={{ fontSize: 11 }}>
            {isDraft ? `DRAFT ${guide.version || ''} — under review` : `Approved v${guide.version}${guide.approved_by_name ? ` by ${guide.approved_by_name}` : ''}`}
          </span>
        </h2>
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>
          {mandatory.length > 0 && `${ticked}/${mandatory.length} mandatory items ticked`}
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 8 }}>
          {isDraft && (
            <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, fontSize: 13, background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A' }}>
              This guide is a draft awaiting review by Paul Kenna Law and Joanne Law. Use it as a prompt, not as the final word — and note anything you disagree with in your assessor notes so the guide can be corrected.
            </div>
          )}
          <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
            Confidential assessor material — never paste into Canvas or show to students. The AI review does not yet use this guide.
          </p>

          {g.task_summary && <p style={{ marginBottom: 10 }}>{g.task_summary}</p>}
          {g.evidence_expected && (
            <p style={{ marginBottom: 10 }}><strong>Evidence expected:</strong> {g.evidence_expected}</p>
          )}
          {g.law_or_standards_engaged && (
            <p style={{ marginBottom: 10, fontSize: 13, color: '#374151' }}><strong>Law and standards engaged:</strong> {g.law_or_standards_engaged}</p>
          )}

          {g.answer_key && g.answer_key.length > 0 && (
            <div style={{ margin: '14px 0', padding: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6 }}>
              <h3 style={{ fontSize: 15, margin: '0 0 4px', color: '#1E3A8A' }}>Answer key — every scenario in the pool</h3>
              <p style={{ fontSize: 12, color: '#1E40AF', marginBottom: 8 }}>First check which scenario(s) this student was given (the question text in SpeedGrader names them), then mark against the matching row.</p>
              {g.answer_key.map((row, i) => (
                <div key={i} style={{ marginBottom: 10, paddingBottom: 8, borderBottom: i < g.answer_key.length - 1 ? '1px solid #DBEAFE' : 'none' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{row.scenario}</div>
                  <div style={{ fontSize: 13 }}><strong>Certificate / answer:</strong> {row.certificate}</div>
                  {row.issued_to && <div style={{ fontSize: 13 }}><strong>Issued to:</strong> {row.issued_to}</div>}
                  {row.matters_in_dispute && <div style={{ fontSize: 13 }}><strong>Matters in dispute (model):</strong> {row.matters_in_dispute}</div>}
                  {row.notes && <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>{row.notes}</div>}
                </div>
              ))}
            </div>
          )}

          <h3 style={{ fontSize: 15, margin: '14px 0 6px', color: '#003040' }}>{labels.mandatory}</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {mandatory.map(m => (
              <li key={m.id || m.text} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
                <input type="checkbox" checked={!!checked[m.id]} onChange={() => toggle(m.id)} style={{ marginTop: 4 }} title="Tick as you verify this item — for your own working, not saved" />
                <span style={{ textDecoration: checked[m.id] ? 'line-through' : 'none', color: checked[m.id] ? '#9CA3AF' : 'inherit' }}>
                  <strong>{m.id}</strong>{m.id ? ' — ' : ''}{m.text}
                </span>
              </li>
            ))}
          </ul>

          {g.nice_to_have && g.nice_to_have.length > 0 && (
            <>
              <h3 style={{ fontSize: 15, margin: '14px 0 6px', color: '#003040' }}>{labels.nice}</h3>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {g.nice_to_have.map(n => <li key={n.id || n.text} style={{ marginBottom: 4 }}><strong>{n.id}</strong>{n.id ? ' — ' : ''}{n.text}</li>)}
              </ul>
            </>
          )}

          {g.watch_points && g.watch_points.length > 0 && (
            <>
              <h3 style={{ fontSize: 15, margin: '14px 0 6px', color: '#991B1B' }}>Watch points</h3>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {g.watch_points.map((w, i) => <li key={i} style={{ marginBottom: 4 }}>{w}</li>)}
              </ul>
            </>
          )}

          {g.sample_strong_answer && (
            <div style={{ marginTop: 14 }}>
              <button className="btn btn-secondary btn-sm" type="button" onClick={() => setShowSample(s => !s)} style={{ fontSize: 12 }}>
                {showSample ? 'Hide' : 'Show'} {labels.sample.toLowerCase()}
              </button>
              {showSample && <p style={{ marginTop: 8, padding: 12, background: '#F9FAFB', borderRadius: 6, fontSize: 13 }}>{g.sample_strong_answer}</p>}
            </div>
          )}

          {g.supplementary_question_example && (
            <div style={{ marginTop: 14 }}>
              <h3 style={{ fontSize: 15, margin: '0 0 6px', color: '#003040' }}>{labels.supp}</h3>
              <p style={{ fontSize: 13, fontStyle: 'italic', color: '#374151' }}>{g.supplementary_question_example}</p>
            </div>
          )}

          {g.assessor_check_required && g.assessor_check_required.length > 0 && (
            <div style={{ marginTop: 14, padding: 12, background: '#FFFBEB', borderRadius: 6, border: '1px solid #FDE68A' }}>
              <h3 style={{ fontSize: 14, margin: '0 0 6px', color: '#92400E' }}>{labels.check}</h3>
              <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13 }}>
                {g.assessor_check_required.map((w, i) => <li key={i} style={{ marginBottom: 4 }}>{w}</li>)}
              </ul>
            </div>
          )}

          <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 12 }}>
            Source: {guide.source || 'marking guide register'} · Canvas assignment {guide.title ? `“${guide.title}”` : ''}
          </p>
        </div>
      )}
    </div>
  );
}
